<script setup>
// AgentLiveReportContainer.vue
import { onMounted, ref, computed } from 'vue';
import { useToggle } from '@vueuse/core';

import AgentTable from './overview/AgentTable.vue';
import MetricCard from './overview/MetricCard.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentConversationMetric = useMapGetter('getAgentConversationMetric');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const showOnlineOnly = ref(false);
const selectedTeam = ref(null);
const [showDropdown, toggleDropdown] = useToggle();

// Obter membros do time selecionado
const teamMembers = computed(() => {
  if (!selectedTeam.value) return [];
  return store.getters['teamMembers/getTeamMembers'](selectedTeam.value);
});

// IDs dos agentes membros do time
const teamMemberIds = computed(() => {
  return teamMembers.value.map(member => member.id);
});

// Lista de times para o dropdown
const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

// Label do time selecionado
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});

// Computed property para filtrar agentes
const filteredAgents = computed(() => {
  let filtered = agents.value;

  // Filtro por time - filtra pelos agentes que são membros do time
  if (selectedTeam.value && teamMemberIds.value.length > 0) {
    filtered = filtered.filter(agent => 
      teamMemberIds.value.includes(agent.id)
    );
  }

  // Filtro por status online
  if (showOnlineOnly.value) {
    filtered = filtered.filter(agent => agent.availability_status === 'online');
  }

  return filtered;
});

const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAgentConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);

const handleAction = async ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  
  // Buscar membros do time quando um time é selecionado
  if (value) {
    await store.dispatch('teamMembers/get', {
      teamId: value,
    });
  }
  
  fetchData();
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-row flex-wrap max-w-full">
    <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')">
      <template #control>
        <div class="flex items-center gap-3">
          <!-- Filtro de online -->
          <div class="flex items-center gap-2">
            <Checkbox v-model="showOnlineOnly" />
            <label 
              class="text-n-slate-12 font-medium text-sm"
              @click="showOnlineOnly = !showOnlineOnly"
            >
              {{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.SHOW_ONLINE_ONLY') }}
            </label>
          </div>

          <!-- Filtro de time -->
          <div
            v-if="teams.length"
            v-on-clickaway="() => toggleDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedTeamLabel"
              class="capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showDropdown"
              :menu-items="teamMenuList"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
              label-class="capitalize"
              @action="handleAction($event)"
            />
          </div>
          
        </div>
      </template>

      <AgentTable
        :agents="filteredAgents"
        :agent-metrics="agentConversationMetric"
        :is-loading="uiFlags.isFetchingAgentConversationMetric"
      />
    </MetricCard>
  </div>
</template>